// PoolContext.tsx
import type React from "react"
import { createContext, useState, useContext, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { getPoolById, type Pool } from "@/api/pools-api"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { QUOTE_TOKENS } from "@/settings"
import { sortPoolAssets } from "@/utils/flipper"

interface TokenInfo {
  denom: number
  ticker: string
}

interface PoolContextType {
  pool: Pool
  setPool: React.Dispatch<React.SetStateAction<Pool>>
  isReversed: boolean
  isFlipped: boolean
  reversePool: () => void
  loading: boolean
  baseToken: string
  quoteToken: string
  baseTokenInfo: TokenInfo
  quoteTokenInfo: TokenInfo
}

const PoolContext = createContext<PoolContextType | undefined>(undefined)

export const PoolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pool, setPool] = useState<Pool | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const { poolId } = useParams<{ poolId: string }>()
  const [isFlipped, setIsFlipped] = useState(false)
  const [baseToken, setBaseToken] = useState<string>("")
  const [quoteToken, setQuoteToken] = useState<string>("")
  const [baseTokenInfo, setBaseTokenInfo] = useState<TokenInfo>({
    denom: 18,
    ticker: "BT",
  })
  const [quoteTokenInfo, setQuoteTokenInfo] = useState<TokenInfo>({
    denom: 18,
    ticker: "QT",
  })
  const navigate = useNavigate()

  const [isReversed, setIsReversed] = useState(false)

  useEffect(() => {
    if (!poolId) {
      navigate("/pool-not-found")
      return
    }

    setLoading(true)

    getPoolById(poolId)
      .then((fetchedPool) => {
        if (fetchedPool) {
          setPool(fetchedPool)

          const sortedTokens = sortPoolAssets({
            token0: fetchedPool.token0,
            token1: fetchedPool.token1,
            token0Ticker: fetchedPool.token0_ticker,
            token1Ticker: fetchedPool.token1_ticker,
          })

          const needsFlipping = sortedTokens.base === fetchedPool.token0

          if (needsFlipping) {
            setBaseToken(fetchedPool.token0)
            setQuoteToken(fetchedPool.token1)
            setBaseTokenInfo({
              denom: fetchedPool.token0_denominator,
              ticker: fetchedPool.token0_ticker,
            })
            setQuoteTokenInfo({
              denom: fetchedPool.token1_denominator,
              ticker: fetchedPool.token1_ticker,
            })
            setIsFlipped(true)
          } else {
            setBaseToken(fetchedPool.token1)
            setQuoteToken(fetchedPool.token0)
            setBaseTokenInfo({
              denom: fetchedPool.token1_denominator,
              ticker: fetchedPool.token1_ticker,
            })
            setQuoteTokenInfo({
              denom: fetchedPool.token0_denominator,
              ticker: fetchedPool.token0_ticker,
            })
          }
        } else {
          navigate("/pool-not-found")
        }
      })
      .catch((error) => {
        console.error("Error fetching pool:", error)
        navigate("/pool-not-found")
      })
      .finally(() => setLoading(false))
  }, [poolId, navigate])

  // Don't render anything while loading or if pool is undefined
  if (loading || !pool) {
    return <LoadingSpinner />
  }

  function reversePool() {
    if (!pool) return

    const oldBase = baseToken
    const oldBaseInfo = baseTokenInfo
    setBaseToken(quoteToken)
    setQuoteToken(oldBase)
    setBaseTokenInfo(quoteTokenInfo)
    setQuoteTokenInfo(oldBaseInfo)
    setIsReversed(!isReversed)
  }

  // At this point, pool is guaranteed to be defined
  return (
    <PoolContext.Provider
      value={{
        pool,
        setPool: setPool as React.Dispatch<React.SetStateAction<Pool>>,
        loading,
        isReversed,
        isFlipped,
        reversePool,
        baseToken,
        quoteToken,
        baseTokenInfo,
        quoteTokenInfo,
      }}
    >
      {children}
    </PoolContext.Provider>
  )
}

export const usePool = () => {
  const context = useContext(PoolContext)
  if (context === undefined) {
    throw new Error("usePool must be used within a PoolProvider")
  }
  return context
}
